/**
 * Erfassungsseite (F2/F4–F8, Δ Ninox): Fremd-Tab statt iframe.
 *
 * Ninox ist nicht einbettbar (CSP frame-ancestors) — die Anwendung öffnet
 * per Deep-Link in einem separaten Tab. Der Screenshot kommt über die
 * Tab-Freigabe (getDisplayMedia, der Melder wählt den Ninox-Tab). Der
 * Kontext (F8) wird zweistufig erfasst: Tabellen-Auswahl (primär) +
 * optional eingefügte Ninox-URL — ein Auslesen des Fremd-Tabs ist
 * cross-origin unmöglich.
 */

import { useEffect, useMemo, useRef, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  ArrowRight,
  Camera,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  GripVertical,
  Maximize2,
  Minimize2,
  MonitorUp,
  Pencil,
  Unplug,
} from "lucide-react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import { AnnotationEditor } from "@/components/erfassung/AnnotationEditor"
import { ErfassungsChat } from "@/components/erfassung/ErfassungsChat"
import { ScreenshotBild } from "@/components/erfassung/ScreenshotBild"
import { ninoxUrlParsen } from "@/lib/ninoxUrl"
import { NinoxTabCapture } from "@/lib/screenshot"
import { api, apiError } from "@/services/api"
import type {
  Annotation,
  Application,
  ChatNachricht,
  Prioritaet,
  Report,
  ReportKontext,
  ReportTyp,
  Screenshot,
} from "@/types"

const LOGIN_HINWEIS_KEY = "ninoxLoginHinweisGesehen"

export function ErfassungPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const entwurfParam = searchParams.get("entwurf")

  const dateiInputRef = useRef<HTMLInputElement>(null)

  // Δ Ninox: persistente Verbindung zum freigegebenen Ninox-Tab (F4).
  // Einmal verbinden → danach Sofort-Abgriff ohne Dialog/Tab-Wechsel.
  const captureRef = useRef<NinoxTabCapture | null>(null)
  const previewRef = useRef<HTMLVideoElement>(null)
  const [tabVerbunden, setTabVerbunden] = useState(false)
  const [tabVerbindet, setTabVerbindet] = useState(false)

  const [appId, setAppId] = useState<number | null>(null)
  const [entwurfId, setEntwurfId] = useState<number | null>(
    entwurfParam ? Number(entwurfParam) : null
  )
  const [rohBild, setRohBild] = useState<Blob | null>(null)
  const [screenshots, setScreenshots] = useState<Screenshot[]>([])
  const [chatNachrichten, setChatNachrichten] = useState<ChatNachricht[]>([])
  const [kiVorschau, setKiVorschau] = useState<Report | null>(null)

  // F8 — betroffene Ninox-Stelle: Tabellen-Auswahl (primär) + optionale URL
  const [tabelleId, setTabelleId] = useState<string>("")
  const [ninoxUrlEingabe, setNinoxUrlEingabe] = useState("")
  // F2a — Einmal-Hinweis: Ninox hat einen eigenen Login (kein SSO)
  const [loginHinweis, setLoginHinweis] = useState(
    () => localStorage.getItem(LOGIN_HINWEIS_KEY) !== "1"
  )
  const loginHinweisSchliessen = () => {
    localStorage.setItem(LOGIN_HINWEIS_KEY, "1")
    setLoginHinweis(false)
  }

  // Formular-Zustand — Typ bewusst OHNE Vorauswahl (setzt die KI-Strukturierung)
  const [typ, setTyp] = useState<ReportTyp | null>(null)
  const [titel, setTitel] = useState("")
  const [istBeschreibung, setIstBeschreibung] = useState("")
  const [sollBeschreibung, setSollBeschreibung] = useState("")
  const [prioritaet, setPrioritaet] = useState<Prioritaet>("mittel")

  // „Neue Meldung" als bewegliches Fenster (mehr Überblick) — geführte
  // Strecke in drei Tabs: Screenshot (volle Größe) → Weiter →
  // Erfassungs-Assistent → KI-Übergabe → Ergebnis (Mini-PRD)
  const [alsFenster, setAlsFenster] = useState(false)
  const [fensterTab, setFensterTab] = useState<"screenshot" | "dialog" | "ergebnis">("screenshot")
  const [screenshotIndex, setScreenshotIndex] = useState(0)
  const [fensterPos, setFensterPos] = useState({ x: 80, y: 70 })
  const ziehenRef = useRef<{ dx: number; dy: number } | null>(null)
  const fensterZiehen = (e: React.PointerEvent) => {
    ziehenRef.current = { dx: e.clientX - fensterPos.x, dy: e.clientY - fensterPos.y }
    const move = (ev: PointerEvent) => {
      const start = ziehenRef.current
      if (!start) return
      setFensterPos({
        x: Math.max(0, Math.min(window.innerWidth - 200, ev.clientX - start.dx)),
        y: Math.max(0, Math.min(window.innerHeight - 80, ev.clientY - start.dy)),
      })
    }
    const up = () => {
      ziehenRef.current = null
      window.removeEventListener("pointermove", move)
      window.removeEventListener("pointerup", up)
    }
    window.addEventListener("pointermove", move)
    window.addEventListener("pointerup", up)
  }

  const { data: anwendungen } = useQuery({
    queryKey: ["applications"],
    queryFn: api.applications.list,
  })
  const anwendung: Application | undefined = useMemo(() => {
    if (appId !== null) return anwendungen?.find((a) => a.id === appId)
    if (!anwendungen || anwendungen.length === 0) return undefined
    if (anwendungen.length === 1) return anwendungen[0]
    // Δ Ninox: Mehrere Anwendungen → IMMER explizite Auswahl. Ein
    // Origin-Match wie im Original greift nicht — die Anwendungen laufen
    // extern bei Ninox, nicht unter der Portal-Domain. KEIN stiller
    // Fallback: ein falsch zugeordneter Report landet im falschen Repo.
    return undefined
  }, [anwendungen, appId])

  // F8: Tabellen der gewählten Anwendung (kuratierte Liste, nur ID + Name)
  const { data: tabellen } = useQuery({
    queryKey: ["applications", anwendung?.id, "tabellen"],
    queryFn: () => api.applications.tabellen(anwendung!.id),
    enabled: anwendung !== undefined,
    staleTime: 5 * 60 * 1000,
  })

  // F8 (Δ Ninox): eingefügte URL → Namen auflösen (DEV- und LIVE-URLs)
  const urlTeile = useMemo(() => ninoxUrlParsen(ninoxUrlEingabe), [ninoxUrlEingabe])
  const { data: aufloesung } = useQuery({
    queryKey: [
      "applications",
      anwendung?.id,
      "url-aufloesung",
      urlTeile?.teamId,
      urlTeile?.databaseId,
      urlTeile?.tabelleId,
    ],
    queryFn: ({ signal }) =>
      api.applications.urlAufloesen(
        anwendung!.id,
        {
          team_id: urlTeile!.teamId!,
          db_id: urlTeile!.databaseId!,
          ...(urlTeile?.tabelleId ? { tabelle_id: urlTeile.tabelleId } : {}),
        },
        signal
      ),
    enabled: anwendung !== undefined && !!urlTeile?.teamId && !!urlTeile?.databaseId,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  // Aufgelöste Tabelle automatisch vorwählen (nur solange nichts gewählt ist)
  useEffect(() => {
    if (aufloesung?.tabelleRef && !tabelleId) {
      setTabelleId(aufloesung.tabelleRef)
    }
  }, [aufloesung, tabelleId])

  // Entwurf fortsetzen (?entwurf=<id>)
  useQuery({
    queryKey: ["reports", entwurfId],
    queryFn: async () => {
      const detail = await api.reports.get(entwurfId!)
      setAppId(detail.applicationId)
      setTabelleId((detail.kontext?.tabelleId as string) ?? "")
      setNinoxUrlEingabe((detail.kontext?.ninoxUrl as string) ?? "")
      setTyp(detail.typ)
      setTitel(detail.titel)
      setIstBeschreibung(detail.istBeschreibung ?? "")
      setSollBeschreibung(detail.sollBeschreibung ?? "")
      setPrioritaet(detail.prioritaet)
      setScreenshots(detail.screenshots)
      setChatNachrichten(detail.chat)
      return detail
    },
    enabled: entwurfId !== null && titel === "" && screenshots.length === 0,
    staleTime: Infinity,
  })

  /** F8 (Δ Ninox): Kontext aus App-Zuordnung, Tabellen-Auswahl + Ninox-URL.

      Ein Auslesen des Ninox-Tabs (Route/Version) ist cross-origin unmöglich —
      der Kontext entsteht aus der Auswahl des Melders. */
  const kontextAktuell = (app: Application): ReportKontext => {
    const geparst = ninoxUrlParsen(ninoxUrlEingabe)
    const tabelle = tabellen?.find((t) => t.id === tabelleId)
    return {
      ninoxTeamId: geparst?.teamId ?? null,
      ninoxDatabaseId: geparst?.databaseId ?? null,
      tabelleId: tabelle?.id ?? geparst?.tabelleId ?? null,
      tabelleName: tabelle?.name ?? null,
      viewId: geparst?.viewId ?? null,
      recordId: geparst?.recordId ?? null,
      ninoxUrl: ninoxUrlEingabe.trim() || null,
      // Screenshot-Umgebung (Δ Ninox): live = echte Daten, dev = Testdaten
      umgebung,
      browser: navigator.userAgent,
      zeitpunkt: new Date().toISOString(),
      // Referenz für die Einordnung: welche App war gewählt
      applicationUrl: app.url,
    }
  }

  const beginnen = useMutation({
    mutationFn: async () => {
      if (!anwendung) throw new Error("Keine Anwendung")
      return api.reports.create({ applicationId: anwendung.id, kontext: kontextAktuell(anwendung) })
    },
    onSuccess: (report) => setEntwurfId(report.id),
    onError: (e) => toast.error(apiError(e)),
  })

  // Δ Ninox: Screenshot-Ziel LIVE oder DEV. Struktur-Analyse bleibt immer DEV;
  // nur der geöffnete Tab (echte Daten vs. Testdaten) unterscheidet sich.
  const [umgebung, setUmgebung] = useState<"live" | "dev">("dev")
  const inNinoxOeffnen = (ziel: "live" | "dev") => {
    if (!anwendung) return
    const url = ziel === "live" && anwendung.urlLive ? anwendung.urlLive : anwendung.url
    setUmgebung(ziel === "live" && anwendung.urlLive ? "live" : "dev")
    window.open(url, "_blank", "noopener")
  }

  // Capture-Session lazy anlegen + beim Verlassen aufräumen
  if (captureRef.current === null) captureRef.current = new NinoxTabCapture()
  useEffect(() => {
    const capture = captureRef.current!
    capture.onEnded = () => setTabVerbunden(false)
    return () => capture.trennen()
  }, [])

  // Live-Vorschau an das <video> hängen, sobald verbunden
  useEffect(() => {
    if (tabVerbunden && previewRef.current) {
      previewRef.current.srcObject = captureRef.current!.mediaStream
    }
  }, [tabVerbunden])

  const tabVerbinden = async () => {
    setTabVerbindet(true)
    try {
      await captureRef.current!.verbinden()
      setTabVerbunden(true)
    } catch (e) {
      // Abbruch im Freigabe-Dialog ist kein Fehler
      if ((e as Error).name !== "NotAllowedError")
        toast.error("Ninox-Tab konnte nicht verbunden werden")
    } finally {
      setTabVerbindet(false)
    }
  }

  const screenshotAufnehmen = async () => {
    if (!captureRef.current?.verbunden) {
      void tabVerbinden()
      return
    }
    try {
      setRohBild(await captureRef.current.frameAbgreifen())
    } catch {
      // Verbindung verloren (Tab geschlossen / Teilen beendet) → neu verbinden
      setTabVerbunden(false)
      toast.error("Verbindung zum Ninox-Tab verloren — bitte neu verbinden")
    }
  }

  const annotationFertig = useMutation({
    mutationFn: ({ bild, annotationen }: { bild: Blob; annotationen: Annotation[] }) =>
      api.reports.screenshots.upload(entwurfId!, bild, annotationen),
    onSuccess: (screenshot) => {
      setScreenshots((s) => [...s, screenshot])
      setRohBild(null)
      toast.success("Screenshot hinzugefügt")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const screenshotEntfernen = useMutation({
    mutationFn: (screenshotId: number) => api.reports.screenshots.remove(entwurfId!, screenshotId),
    onSuccess: (_d, screenshotId) =>
      setScreenshots((s) => s.filter((x) => x.id !== screenshotId)),
    onError: (e) => toast.error(apiError(e)),
  })

  // Nachbearbeitung eines gespeicherten Screenshots (Fenster-Tab 1):
  // Bild laden → Annotation-Editor → Übernehmen ersetzt das Bild
  const [bearbeitung, setBearbeitung] = useState<{ blob: Blob; altId: number } | null>(null)

  const bearbeitenOeffnen = async (screenshotId: number) => {
    try {
      const blob = await api.reports.screenshots.datei(entwurfId!, screenshotId)
      setBearbeitung({ blob, altId: screenshotId })
    } catch (e) {
      toast.error(apiError(e))
    }
  }

  const bearbeitungFertig = useMutation({
    mutationFn: async ({
      bild,
      annotationen,
      altId,
    }: {
      bild: Blob
      annotationen: Annotation[]
      altId: number
    }) => {
      const neu = await api.reports.screenshots.upload(entwurfId!, bild, annotationen)
      await api.reports.screenshots.remove(entwurfId!, altId)
      return neu
    },
    onSuccess: (neu, { altId }) => {
      setScreenshots((s) => [...s.filter((x) => x.id !== altId), neu])
      setScreenshotIndex(Math.max(0, screenshots.length - 1))
      setBearbeitung(null)
      toast.success("Screenshot aktualisiert")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  // §8: Abschluss → finalize → Mini-PRD-Vorschau (Felder werden vorbefüllt)
  const kiZusammenfassen = useMutation({
    mutationFn: async () => {
      // Manuelle Eingaben zuerst sichern — sie fließen in die Strukturierung ein
      await api.reports.update(entwurfId!, {
        typ: typ ?? undefined,
        titel: titel || undefined,
        istBeschreibung: istBeschreibung || null,
        sollBeschreibung: sollBeschreibung || null,
        kontext: anwendung ? kontextAktuell(anwendung) : undefined,
      })
      return api.reports.finalize(entwurfId!)
    },
    onSuccess: (report) => {
      setTitel(report.titel)
      setTyp(report.typ)
      setIstBeschreibung(report.istBeschreibung ?? "")
      setSollBeschreibung(report.sollBeschreibung ?? "")
      setPrioritaet(report.prioritaet)
      setKiVorschau(report)
      // Im Fenster-Modus direkt zum Ergebnis-Tab springen
      setFensterTab("ergebnis")
      toast.success("Mini-PRD erstellt — bitte prüfen und absenden")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const absenden = useMutation({
    mutationFn: async () => {
      await api.reports.update(entwurfId!, {
        typ: typ ?? undefined,
        titel,
        istBeschreibung: istBeschreibung || null,
        sollBeschreibung: sollBeschreibung || null,
        prioritaet,
        kontext: anwendung ? kontextAktuell(anwendung) : undefined,
      })
      return api.reports.absenden(entwurfId!)
    },
    onSuccess: (report) => {
      void queryClient.invalidateQueries({ queryKey: ["reports"] })
      toast.success("Meldung abgesendet")
      navigate(`/meldungen/${report.id}`)
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const entwurfVerwerfen = useMutation({
    mutationFn: () => api.reports.remove(entwurfId!),
    onSuccess: () => {
      setEntwurfId(null)
      setScreenshots([])
      setTitel("")
      setIstBeschreibung("")
      setSollBeschreibung("")
      toast.success("Entwurf verworfen")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  if (!anwendungen) return <p className="text-muted-foreground">Lade Anwendungen …</p>
  if (anwendungen.length === 0) {
    return (
      <p className="rounded-md bg-warning/10 px-4 py-3 text-warning">
        Keine aktive Anwendung konfiguriert — Anlage durch netz-fabrik (F3).
      </p>
    )
  }
  if (!anwendung) {
    // Laufzeit-Guard: Domain keiner Anwendung zugeordnet → explizite Auswahl
    return (
      <div className="mx-auto max-w-lg pt-10">
        <h1 className="mb-1.5 text-[22px] font-bold">Welche Anwendung möchtest du testen?</h1>
        <p className="mb-5 text-sm text-muted-foreground">
          Für dieses Projekt sind mehrere Ninox-Anwendungen hinterlegt — bitte wähle aus,
          damit die Meldung an der richtigen Stelle landet.
        </p>
        <div className="flex flex-col gap-2.5">
          {anwendungen.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => setAppId(a.id)}
              className="flex items-center justify-between rounded-xl border bg-card px-5 py-4 text-left shadow-card transition-colors hover:border-primary/40"
            >
              <span>
                <span className="block text-[15px] font-semibold">{a.name}</span>
                <span className="mt-0.5 block text-[13px] text-muted-foreground">{a.url}</span>
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/50" />
            </button>
          ))}
        </div>
      </div>
    )
  }

  const absendenMoeglich =
    typ !== null &&
    titel.trim() !== "" &&
    (istBeschreibung.trim() !== "" || sollBeschreibung.trim() !== "")

  return (
    <div className="flex gap-4" style={{ height: "calc(100vh - 8rem)" }}>
      {/* Δ Ninox (F2): Anwendung läuft im Fremd-Tab — Panel mit Deep-Link,
          Anleitung zur Tab-Freigabe und Einmal-Hinweis zum Ninox-Login (F2a) */}
      <div className="relative flex min-w-0 flex-1 flex-col overflow-hidden rounded-xl border bg-card">
        <div className="flex h-11 shrink-0 items-center gap-2 border-b bg-muted px-4">
          <span className="h-[9px] w-[9px] rounded-full bg-border" />
          <span className="h-[9px] w-[9px] rounded-full bg-border" />
          <span className="h-[9px] w-[9px] rounded-full bg-border" />
          <span className="ml-2 truncate text-xs text-muted-foreground/80">
            {anwendung.url.replace(/^https?:\/\//, "")}
          </span>
        </div>
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-5 overflow-y-auto px-8 py-10">
          <div className="max-w-xl text-center">
            <h1 className="text-[22px] font-bold">{anwendung.name}</h1>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Deine Ninox-Anwendung läuft in einem eigenen Browser-Tab — sie kann hier
              nicht eingebettet werden. Öffne sie, arbeite dort wie gewohnt und komm zum
              Melden hierher zurück.
            </p>
          </div>
          {anwendung.urlLive ? (
            // Δ Ninox: zwei Ziele — Live (echte Daten) und DEV (Testdaten)
            <div className="flex flex-col items-center gap-2">
              <div className="flex flex-wrap items-center justify-center gap-2.5">
                <button
                  type="button"
                  onClick={() => inNinoxOeffnen("live")}
                  className="flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-[15px] font-bold text-primary-foreground shadow-card hover:bg-primary-hover"
                >
                  <ExternalLink className="h-4 w-4" />
                  Ninox Live öffnen
                </button>
                <button
                  type="button"
                  onClick={() => inNinoxOeffnen("dev")}
                  className="flex items-center gap-2 rounded-xl bg-secondary px-6 py-3 text-[15px] font-bold hover:bg-secondary/80"
                >
                  <ExternalLink className="h-4 w-4" />
                  Ninox DEV öffnen
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                <span className="font-semibold">Live</span> = echte Daten,{" "}
                <span className="font-semibold">DEV</span> = Testdaten. Die Struktur ist in
                beiden identisch.
              </p>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => inNinoxOeffnen("dev")}
              className="flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-[15px] font-bold text-primary-foreground shadow-card hover:bg-primary-hover"
            >
              <ExternalLink className="h-4 w-4" />
              In Ninox öffnen
            </button>
          )}
          {loginHinweis && (
            <div className="flex max-w-xl items-start gap-3 rounded-[10px] border border-warning/30 bg-warning/10 px-4 py-3 text-left text-[13px]">
              <span className="min-w-0">
                <span className="font-semibold">Hinweis:</span> Ninox hat einen{" "}
                <span className="font-semibold">eigenen Login</span> — melde dich dort mit
                deinem Ninox-Konto an. Deine Anmeldung hier im Portal gilt dort nicht.
              </span>
              <button
                type="button"
                onClick={loginHinweisSchliessen}
                className="shrink-0 rounded bg-secondary px-2 py-1 text-[11px] font-bold hover:bg-secondary/80"
              >
                Verstanden
              </button>
            </div>
          )}
          <ol className="max-w-xl space-y-2.5 rounded-[10px] border bg-muted/30 px-5 py-4 text-[13px] text-muted-foreground">
            <li className="flex gap-2.5">
              <span className="font-bold text-foreground">1.</span>
              <span>
                <span className="font-semibold text-foreground">In Ninox öffnen</span> und dort
                zur Stelle navigieren, um die es geht (Tabelle, Ansicht, Datensatz).
              </span>
            </li>
            <li className="flex gap-2.5">
              <span className="font-bold text-foreground">2.</span>
              <span>
                <span className="font-semibold text-foreground">„Melden“</span> starten und den
                Ninox-Tab <span className="font-semibold text-foreground">einmal verbinden</span>{" "}
                (der Browser fragt, welcher Tab geteilt wird — den Ninox-Tab wählen).
              </span>
            </li>
            <li className="flex gap-2.5">
              <span className="font-bold text-foreground">3.</span>
              <span>
                Danach genügt ein Klick auf die <span className="font-semibold text-foreground">
                Live-Vorschau</span> für jeden Screenshot — ohne erneuten Dialog, ohne Tab-Wechsel.
                Markieren, beschreiben, absenden.
              </span>
            </li>
          </ol>

          {/* Δ Ninox: Live-Vorschau des verbundenen Tabs = Auslöser (F4) */}
          {entwurfId !== null && tabVerbunden && (
            <div className="w-full max-w-xl">
              <button
                type="button"
                onClick={() => void screenshotAufnehmen()}
                title="Klicken für einen Screenshot des Ninox-Tabs"
                className="group relative block w-full overflow-hidden rounded-xl border-2 border-primary/30 bg-black shadow-card transition-colors hover:border-primary"
              >
                <video
                  ref={previewRef}
                  autoPlay
                  muted
                  playsInline
                  className="aspect-video w-full object-contain"
                />
                <span className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-center gap-2 bg-gradient-to-t from-black/70 to-transparent py-3 text-[13px] font-bold text-white">
                  <Camera className="h-4 w-4" /> Screenshot aufnehmen
                </span>
                <span className="absolute left-2 top-2 flex items-center gap-1 rounded-full bg-primary/90 px-2 py-0.5 text-[11px] font-bold text-primary-foreground">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> Ninox-Tab live
                </span>
              </button>
              <div className="mt-1.5 flex items-center justify-between text-[12px] text-muted-foreground">
                <span>Verbunden — Vorschau anklicken für jeden weiteren Screenshot.</span>
                <button
                  type="button"
                  onClick={() => {
                    captureRef.current?.trennen()
                    setTabVerbunden(false)
                  }}
                  className="flex items-center gap-1 font-semibold hover:text-foreground"
                >
                  <Unplug className="h-3 w-3" /> Verbindung trennen
                </button>
              </div>
            </div>
          )}
        </div>
        {/* Schwebende Melden-Leiste (§8) */}
        <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-2.5 rounded-full border bg-card px-3.5 py-2.5 shadow-[0_8px_24px_rgba(15,23,42,0.14)]">
          <span className="whitespace-nowrap pl-1 text-[13px] font-semibold">{anwendung.name}</span>
          {entwurfId === null ? (
            <button
              type="button"
              onClick={() => beginnen.mutate()}
              disabled={beginnen.isPending}
              className="rounded-full bg-primary px-4 py-2 text-[13px] font-semibold text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
            >
              Melden
            </button>
          ) : (
            <>
              {tabVerbunden ? (
                <button
                  type="button"
                  onClick={() => void screenshotAufnehmen()}
                  className="flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary px-4 py-2 text-[13px] font-semibold text-primary-foreground hover:bg-primary-hover"
                >
                  <Camera className="h-[15px] w-[15px]" />
                  Screenshot
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => void tabVerbinden()}
                  disabled={tabVerbindet}
                  className="flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary px-4 py-2 text-[13px] font-semibold text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
                >
                  <MonitorUp className="h-[15px] w-[15px]" />
                  {tabVerbindet ? "Verbinde …" : "Ninox-Tab verbinden"}
                </button>
              )}
              <button
                type="button"
                onClick={() => dateiInputRef.current?.click()}
                className="whitespace-nowrap rounded-full bg-secondary px-3.5 py-2 text-[13px] font-semibold hover:bg-secondary/80"
                title="Fallback: Bilddatei hochladen"
              >
                Bild hochladen
              </button>
              <input
                ref={dateiInputRef}
                type="file"
                accept="image/png,image/jpeg"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) setRohBild(file)
                  e.target.value = ""
                }}
              />
            </>
          )}
        </div>
      </div>

      {/* Meldungsformular (F6/F22) — Inhalt für Seitenleiste UND Fenster */}
      {entwurfId !== null && (() => {
        // Uppercase-Feldbeschriftung (Design Screen 03)
        const LABEL = "mb-1.5 block text-xs font-bold uppercase tracking-wide text-foreground/70"

        // Bausteine — in Seitenleiste (eine Spalte) und Fenster (zwei Spalten)
        // unterschiedlich angeordnet
        const chatBlock = (
          <ErfassungsChat
            reportId={entwurfId}
            nachrichten={chatNachrichten}
            onNeueNachrichten={setChatNachrichten}
            hoch={alsFenster}
          />
        )

        // KI-Zusammenfassung direkt unter dem Dialog — sie füllt Typ + Felder
        const kiButtonBlock = (
          <button
            type="button"
            onClick={() => kiZusammenfassen.mutate()}
            disabled={
              kiZusammenfassen.isPending ||
              (chatNachrichten.length === 0 && !titel && !istBeschreibung && !sollBeschreibung)
            }
            title="Strukturiert Dialog + Screenshots zum Mini-PRD (F14)"
            className="self-start rounded-lg bg-secondary px-3.5 py-2 text-[13px] font-semibold hover:bg-secondary/80 disabled:opacity-50"
          >
            {kiZusammenfassen.isPending ? "KI strukturiert …" : "KI-Zusammenfassung erstellen"}
          </button>
        )

        const vorschauBlock = kiVorschau && (
            <div className="rounded-[10px] border border-primary/15 bg-primary/5 px-3.5 py-3 text-xs">
              <p className="font-bold text-primary">KI-Zusammenfassung übernommen</p>
              {kiVorschau.akzeptanzkriterien && kiVorschau.akzeptanzkriterien.length > 0 && (
                <ul className="mt-1 list-disc pl-4">
                  {kiVorschau.akzeptanzkriterien.map((k, i) => (
                    <li key={i}>{k}</li>
                  ))}
                </ul>
              )}
              {kiVorschau.machbarkeit && (
                <p className="mt-1 text-muted-foreground">
                  Unverbindliche Einschätzung: Aufwand {kiVorschau.machbarkeit}
                  {kiVorschau.modulId ? ` · Bereich „${kiVorschau.modulId}“` : ""}
                </p>
              )}
              {kiVorschau.duplikatVon && (
                <p className="mt-1 font-medium text-warning">
                  Hinweis: Ähnliche Meldung #{kiVorschau.duplikatVon} existiert bereits — du
                  kannst diesen Entwurf löschen und dich der bestehenden Meldung anschließen.
                </p>
              )}
            </div>
          )

        const typBlock = (
          <div className="flex gap-2">
            {(["fehler", "aenderungswunsch"] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTyp(t)}
                className={`flex-1 rounded-lg px-3 py-2 text-[13px] font-semibold transition-colors ${
                  typ === t ? "bg-primary text-primary-foreground" : "bg-secondary hover:bg-secondary/80"
                }`}
              >
                {t === "fehler" ? "Fehler" : "Änderungswunsch"}
              </button>
            ))}
          </div>
        )

        // F8 (Δ Ninox): betroffene Stelle — Tabellen-Auswahl + optionale URL
        const geparst = ninoxUrlParsen(ninoxUrlEingabe)
        const stelleBlock = (
          <div className="flex flex-col gap-3">
            <label className="text-sm">
              <span className={LABEL}>Betroffene Tabelle</span>
              <select
                value={tabelleId}
                onChange={(e) => setTabelleId(e.target.value)}
                className="w-full rounded-lg border px-3 py-2 text-[13px]"
              >
                <option value="">— bitte wählen —</option>
                {(tabellen ?? []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              {tabellen && tabellen.length === 0 && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Noch keine Tabellenliste hinterlegt — du kannst trotzdem melden; die
                  Zuordnung übernimmt dann netz-fabrik.
                </p>
              )}
            </label>
            <label className="text-sm">
              <span className={LABEL}>Ninox-Link (optional)</span>
              <input
                value={ninoxUrlEingabe}
                onChange={(e) => setNinoxUrlEingabe(e.target.value)}
                placeholder="Adresszeile aus dem Ninox-Tab hier einfügen"
                className="w-full rounded-lg border px-3 py-2 font-mono text-xs"
              />
              {ninoxUrlEingabe.trim() !== "" && (
                <p className={`mt-1 text-xs ${geparst ? "text-muted-foreground" : "text-warning"}`}>
                  {geparst
                    ? aufloesung
                      ? [
                          // Aufgelöste Namen (Katalog + DEV-Struktur, Δ Ninox)
                          aufloesung.teamName && `Team ${aufloesung.teamName}`,
                          `Datenbank ${aufloesung.datenbankName}`,
                          aufloesung.tabelleName
                            ? `Tabelle ${aufloesung.tabelleName}${!tabelleId || tabelleId === aufloesung.tabelleRef ? " (übernommen)" : ""}`
                            : geparst.tabelleId && `Tabelle ${geparst.tabelleId}`,
                          geparst.viewId && `Sicht ${geparst.viewId}`,
                          geparst.recordId && `Datensatz ${geparst.recordId}`,
                        ]
                          .filter(Boolean)
                          .join(" · ")
                      : [
                          geparst.teamId && `Team ${geparst.teamId}`,
                          geparst.databaseId && `DB ${geparst.databaseId}`,
                          geparst.tabelleId && `Tabelle ${geparst.tabelleId}`,
                          geparst.viewId && `Sicht ${geparst.viewId}`,
                          geparst.recordId && `Datensatz ${geparst.recordId}`,
                        ]
                          .filter(Boolean)
                          .join(" · ")
                    : "Kein Ninox-Link erkannt — bitte die komplette Adresszeile einfügen."}
                </p>
              )}
            </label>
          </div>
        )

        const felderBlock = (
          <>
          <label className="text-sm">
            <span className={LABEL}>Titel *</span>
            <input
              value={titel}
              onChange={(e) => setTitel(e.target.value)}
              maxLength={255}
              className="w-full rounded-lg border px-3 py-2 text-[13px]"
              placeholder="Kurz und prägnant"
            />
          </label>

          <label className="text-sm">
            <span className={LABEL}>
              {typ === "aenderungswunsch"
                ? "Heutiger Zustand / Workaround"
                : "Was passiert (Ist)? *"}
            </span>
            <textarea
              value={istBeschreibung}
              onChange={(e) => setIstBeschreibung(e.target.value)}
              rows={3}
              className="w-full rounded-lg border px-3 py-2 text-[13px]"
            />
          </label>

          <label className="text-sm">
            <span className={LABEL}>
              {typ === "aenderungswunsch"
                ? "Gewünschtes Verhalten *"
                : "Was sollte passieren (Soll)?"}
            </span>
            <textarea
              value={sollBeschreibung}
              onChange={(e) => setSollBeschreibung(e.target.value)}
              rows={3}
              className="w-full rounded-lg border px-3 py-2 text-[13px]"
            />
          </label>

          <label className="text-sm">
            <span className={LABEL}>Priorität (aus Kundensicht)</span>
            <select
              value={prioritaet}
              onChange={(e) => setPrioritaet(e.target.value as Prioritaet)}
              className="w-full rounded-lg border px-3 py-2 text-[13px]"
            >
              <option value="niedrig">Niedrig</option>
              <option value="mittel">Mittel</option>
              <option value="hoch">Hoch</option>
            </select>
          </label>
          </>
        )

        const screenshotsBlock = (
          <div className="text-sm">
            <span className={LABEL}>
              Screenshots ({screenshots.length})
            </span>
            <div className="flex flex-col gap-2">
              {screenshots.map((s) => (
                <div key={s.id} className="relative">
                  <ScreenshotBild reportId={entwurfId} screenshot={s} />
                  <button
                    type="button"
                    onClick={() => screenshotEntfernen.mutate(s.id)}
                    className="absolute right-1 top-1 rounded bg-destructive px-1.5 py-0.5 text-[10px] font-bold text-destructive-foreground"
                  >
                    Entfernen
                  </button>
                </div>
              ))}
              {screenshots.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  Noch kein Screenshot — über die Leiste unten aufnehmen.
                </p>
              )}
            </div>
          </div>
        )

        const footerBlock = (
          <div className="mt-auto flex gap-2 pt-2">
            <button
              type="button"
              onClick={() => entwurfVerwerfen.mutate()}
              className="rounded-lg bg-secondary px-3.5 py-2.5 text-[13px] font-semibold hover:bg-secondary/80"
            >
              Entwurf löschen
            </button>
            <button
              type="button"
              onClick={() => absenden.mutate()}
              disabled={!absendenMoeglich || absenden.isPending}
              className="flex-1 rounded-lg bg-primary px-3.5 py-2.5 text-[13px] font-bold text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
            >
              Meldung absenden
            </button>
          </div>
        )

        return alsFenster ? (
          // Bewegliches Fenster (Drag am Kopf): breit + hoch, zwei Tabs —
          // 1) Erfassungs-Assistent (Chat füllt die Höhe), 2) Ergebnis
          // (Mini-PRD-Formular + Vorschau + Screenshots). Nach der
          // KI-Zusammenfassung wechselt das Fenster automatisch auf Tab 2.
          <div
            className="fixed z-40 flex h-[88vh] w-[1400px] max-w-[96vw] flex-col overflow-hidden rounded-[14px] border bg-card shadow-[0_24px_60px_rgba(15,23,42,0.28)]"
            style={{ left: fensterPos.x, top: fensterPos.y }}
          >
            <div
              onPointerDown={fensterZiehen}
              className="flex shrink-0 cursor-move select-none items-center justify-between border-b bg-background py-3 pl-5 pr-4"
            >
              <div className="flex items-center gap-2.5">
                <GripVertical className="h-3.5 w-3.5 text-muted-foreground/60" />
                <h2 className="text-[15px] font-bold">Neue Meldung</h2>
              </div>
              <button
                type="button"
                onClick={() => setAlsFenster(false)}
                onPointerDown={(e) => e.stopPropagation()}
                title="An den Rand andocken"
                className="flex items-center gap-1.5 rounded-lg bg-secondary px-3 py-1.5 text-[13px] font-semibold text-muted-foreground hover:text-foreground"
              >
                <Minimize2 className="h-3.5 w-3.5" /> Andocken
              </button>
            </div>

            {/* Tab-Leiste (geführte Strecke: 1 → 2 → 3) */}
            <div className="flex shrink-0 gap-1 border-b bg-background px-5">
              {(
                [
                  ["screenshot", `Screenshot${screenshots.length > 0 ? ` (${screenshots.length})` : ""}`],
                  ["dialog", "Erfassungs-Assistent"],
                  ["ergebnis", "Ergebnis (Mini-PRD)"],
                ] as const
              ).map(([wert, label]) => (
                <button
                  key={wert}
                  type="button"
                  onClick={() => setFensterTab(wert)}
                  className={`-mb-px flex items-center gap-2 border-b-2 px-4 py-2.5 text-[13px] font-semibold transition-colors ${
                    fensterTab === wert
                      ? "border-primary text-foreground"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {label}
                  {wert === "ergebnis" && kiVorschau && (
                    <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                  )}
                </button>
              ))}
            </div>

            {fensterTab === "screenshot" ? (
              <div className="flex min-h-0 flex-1 flex-col gap-3 p-5">
                {screenshots.length === 0 ? (
                  <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 rounded-[10px] border border-dashed bg-muted/30 px-6 text-center">
                    <p className="max-w-md text-sm text-muted-foreground">
                      Noch kein Screenshot. Nimm ihn über die Leiste unter der
                      Anwendung auf (dazu das Fenster kurz „Andocken“) — oder
                      lade ein Bild hoch.
                    </p>
                    <button
                      type="button"
                      onClick={() => dateiInputRef.current?.click()}
                      className="rounded-lg bg-secondary px-3.5 py-2 text-[13px] font-semibold hover:bg-secondary/80"
                    >
                      Bild hochladen
                    </button>
                  </div>
                ) : (
                  <div className="relative min-h-0 flex-1 overflow-hidden rounded-[10px] border bg-muted/40">
                    <ScreenshotBild
                      gross
                      reportId={entwurfId}
                      screenshot={screenshots[Math.min(screenshotIndex, screenshots.length - 1)]}
                    />
                    <div className="absolute right-2 top-2 flex gap-1.5">
                      <button
                        type="button"
                        onClick={() => {
                          const s = screenshots[Math.min(screenshotIndex, screenshots.length - 1)]
                          void bearbeitenOeffnen(s.id)
                        }}
                        className="flex items-center gap-1 rounded bg-primary px-2 py-1 text-[11px] font-bold text-primary-foreground hover:bg-primary-hover"
                      >
                        <Pencil className="h-3 w-3" /> Bearbeiten
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const s = screenshots[Math.min(screenshotIndex, screenshots.length - 1)]
                          screenshotEntfernen.mutate(s.id)
                          setScreenshotIndex(0)
                        }}
                        className="rounded bg-destructive px-2 py-1 text-[11px] font-bold text-destructive-foreground"
                      >
                        Entfernen
                      </button>
                    </div>
                  </div>
                )}
                <div className="flex shrink-0 items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    {screenshots.length > 0 && (
                      <button
                        type="button"
                        onClick={() => dateiInputRef.current?.click()}
                        className="rounded-lg bg-secondary px-3.5 py-2 text-[13px] font-semibold hover:bg-secondary/80"
                      >
                        Bild hochladen
                      </button>
                    )}
                    {screenshots.length > 1 && (
                      <>
                        <button
                          type="button"
                          title="Vorheriges Bild"
                          onClick={() =>
                            setScreenshotIndex(
                              (Math.min(screenshotIndex, screenshots.length - 1) -
                                1 +
                                screenshots.length) %
                                screenshots.length
                            )
                          }
                          className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary text-muted-foreground hover:text-foreground"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </button>
                        {screenshots.map((s, i) => (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => setScreenshotIndex(i)}
                            className={`h-7 w-7 rounded-full text-xs font-bold ${
                              Math.min(screenshotIndex, screenshots.length - 1) === i
                                ? "bg-primary text-primary-foreground"
                                : "bg-secondary text-muted-foreground hover:text-foreground"
                            }`}
                          >
                            {i + 1}
                          </button>
                        ))}
                        <button
                          type="button"
                          title="Nächstes Bild"
                          onClick={() =>
                            setScreenshotIndex(
                              (Math.min(screenshotIndex, screenshots.length - 1) + 1) %
                                screenshots.length
                            )
                          }
                          className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary text-muted-foreground hover:text-foreground"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setFensterTab("dialog")}
                    className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-[13px] font-bold text-primary-foreground hover:bg-primary-hover"
                  >
                    Weiter zum Erfassungs-Assistenten
                    <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ) : fensterTab === "dialog" ? (
              <div className="flex min-h-0 flex-1 flex-col gap-3 p-5">
                {chatBlock}
                {kiButtonBlock}
              </div>
            ) : (
              <div className="grid min-h-0 flex-1 grid-cols-2 gap-x-6 overflow-y-auto p-5">
                <div className="flex flex-col gap-3">
                  {typBlock}
                  {stelleBlock}
                  {felderBlock}
                  {footerBlock}
                </div>
                <div className="flex flex-col gap-3">
                  {vorschauBlock || (
                    <p className="rounded-[10px] border border-dashed px-3.5 py-3 text-xs text-muted-foreground">
                      Noch keine KI-Zusammenfassung — im Tab „Erfassungs-Assistent“
                      erstellen oder die Felder links manuell ausfüllen.
                    </p>
                  )}
                  {screenshotsBlock}
                </div>
              </div>
            )}
          </div>
        ) : (
          <aside className="flex w-[400px] shrink-0 flex-col gap-3.5 overflow-y-auto rounded-xl border bg-card p-5">
            <div className="flex items-center justify-between">
              <h2 className="text-[15px] font-bold">Neue Meldung</h2>
              <button
                type="button"
                onClick={() => {
                  setFensterPos({
                    x: Math.max(16, Math.round(window.innerWidth / 2 - 700)),
                    y: Math.max(16, Math.round(window.innerHeight * 0.06)),
                  })
                  setFensterTab(kiVorschau ? "ergebnis" : "screenshot")
                  setAlsFenster(true)
                }}
                title="Als bewegliches Fenster öffnen"
                className="flex items-center gap-1.5 rounded-lg px-2 py-1 text-[13px] font-semibold text-muted-foreground hover:bg-secondary hover:text-foreground"
              >
                <Maximize2 className="h-3.5 w-3.5" /> Fenster
              </button>
            </div>
            {chatBlock}
            {kiButtonBlock}
            {vorschauBlock}
            {typBlock}
            {stelleBlock}
            {felderBlock}
            {screenshotsBlock}
            {footerBlock}
          </aside>
        )
      })()}

      {rohBild && entwurfId !== null && (
        <AnnotationEditor
          bild={rohBild}
          onAbbrechen={() => setRohBild(null)}
          onFertig={(e) => annotationFertig.mutate(e)}
        />
      )}

      {/* Nachbearbeitung eines gespeicherten Screenshots (Fenster-Tab 1) */}
      {bearbeitung && entwurfId !== null && (
        <AnnotationEditor
          bild={bearbeitung.blob}
          onAbbrechen={() => setBearbeitung(null)}
          onFertig={(e) => bearbeitungFertig.mutate({ ...e, altId: bearbeitung.altId })}
        />
      )}
    </div>
  )
}
